//* Importing components from react
import { useEffect, useState, useRef } from "react";

//*Importing components from react-bootstrap
import { Button, Form, InputGroup, Row, Col, Spinner } from "react-bootstrap";

//* Importing components
import { Attention } from "../../../component/base/Attention";

//* Importing hooks from react-router-dom
import { useNavigate, useParams } from "react-router-dom";

//* Importing icons from material-ui
import { ArrowBack, SaveRounded } from "@material-ui/icons";

//* Importing hooks
import { useTags } from "../../../hooks/tags/useTags";

//* Importing components
import { ErrorMessages } from "../../../component/common/ErrorMessages";

type Tag = {
  id: string;
  name: string;
  color: string;
};

type TagsFormProps = {
  editMode?: boolean;
};

export function TagsForm({ editMode = false }: TagsFormProps) {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
    color: "#000000",
  });
  const [originalData, setOriginalData] = useState<Tag | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const { getTagById, createTag, updateTag, isCreating, isUpdating } =
    useTags(token);

  useEffect(() => {
    const fetchTagData = async () => {
      if (editMode && params.id) {
        setIsLoadingData(true);
        try {
          const tagData = await getTagById(params.id);
          if (tagData) {
            setFormData({
              name: tagData.name || "",
              color: tagData.color || "#000000",
            });
            setOriginalData(tagData);
          }
        } catch (error) {
          console.error("Erro ao buscar dados da tag:", error);
          setErrorMessages(["Erro ao buscar dados da tag."]);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchTagData();
  }, [editMode, params.id, getTagById]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.color) {
      setErrorMessages(["Por favor, selecione uma cor antes de salvar."]);
      return;
    }

    try {
      if (editMode && params.id) {
        await updateTag({
          id: params.id,
          name: formData.name,
          color: formData.color,
        });
      } else {
        await createTag({
          name: formData.name,
          color: formData.color,
        });
      }
      navigate("/tags");
    } catch (err) {
      console.error("Operação falhou:", err);
      setErrorMessages(["Falha ao salvar a tag. Tente novamente."]);
    }
  };

  const isLoading = isCreating || isUpdating || isLoadingData;
  const clearErrors = () => setErrorMessages([]);

  const colorPresets = [
    // Linha 1 - Tons mais escuros
    "#2E7D32", // Verde Escuro
    "#D84315", // Vermelho Escuro
    "#1E88E5", // Azul Escuro
    "#8E24AA", // Roxo Escuro
    "#F57F17", // Amarelo Escuro
    // Linha 2 - Tons mais claros
    "#66BB6A", // Verde Médio
    "#F44336", // Vermelho Médio
    "#2196F3", // Azul Médio
    "#BA68C8", // Roxo Médio
    "#FFF176", // Amarelo Claro
  ];


  return (
    <div className="row">
      <div className="col-12">
        <div className="py-5 px-3">
          <div className="card-body" id="card">
            <h4 className="mt-0 header-title">
              {editMode ? "Editar" : "Cadastrar"} Tag
            </h4>
            <p>
              {editMode
                ? "Edite uma tag do seu sistema"
                : "Cadastre as tags do seu sistema"}
            </p>
            <br />

            <ErrorMessages messages={errorMessages} clearErrors={clearErrors} />

            {isLoading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "200px" }}
              >
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Carregando...</span>
                </Spinner>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    {/* Campo de Nome */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Nome <Attention />
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Nome"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        required
                      />
                    </Form.Group>

                    {/* Campo de Cor */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Cor <Attention />
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text style={{ cursor: "pointer" }}>
                          <div
                            style={{
                              width: "24px",
                              height: "24px",
                              backgroundColor: formData.color,
                              border: "1px solid #ccc",
                            }}
                            onClick={() => colorInputRef.current?.click()}
                          />
                        </InputGroup.Text>
                        <Form.Control
                          ref={colorInputRef}
                          type="color"
                          value={formData.color}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              color: e.target.value,
                            })
                          }
                          style={{ padding: 0, border: "none" }}
                          required
                        />
                      </InputGroup>
                      {/* Botão Rainbow */}
                      <Button
                        variant="outline-secondary"
                        onClick={() => colorInputRef.current?.click()}
                        style={{
                          background:
                            "linear-gradient(45deg, red, orange, yellow, green, blue, indigo, violet)",
                          border: "none",
                          color: "#fff",
                          marginTop: "10px",
                          width: "100%",
                        }}
                        title="Selecione uma cor"
                      >
                        Cor Personalizada
                      </Button>
                    </Form.Group>

                    {/* Predefinições de Cor em Grade */}
                    <Form.Group className="mb-3">
                      <Form.Label>Predefinições de Cor</Form.Label>
                      <div
                        className="d-grid gap-2 mt-2"
                        style={{
                          gridTemplateColumns: "repeat(5, 1fr)",
                          display: "grid",
                        }}
                      >
                        {colorPresets.map((color, index) => (
                          <div
                            key={index}
                            onClick={() => setFormData({ ...formData, color })}
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: color,
                              borderRadius: "5px",
                              cursor: "pointer",
                              border:
                                formData.color.toLowerCase() ===
                                color.toLowerCase()
                                  ? "3px solid #000"
                                  : "1px solid #ccc",
                              transition: "border 0.3s",
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    </Form.Group>
                  </Col>

                  {/* Pré-visualização da Tag */}
                  <Col
                    md={6}
                    className="d-flex flex-column align-items-center justify-content-center"
                  >
                    <Form.Label>Pré-visualização da Tag</Form.Label>
                    {/* Componente Personalizado para Pré-visualização */}
                    <div
                      style={{
                        backgroundColor: formData.color,
                        color: "#fff",
                        padding: "12px 22px",
                        fontSize: "1.5rem",
                        borderRadius: "1.5rem",
                        display: "inline-block",
                        minWidth: "150px",
                        textAlign: "center",
                      }}
                    >
                      {formData.name || "Nome da Tag"}
                    </div>
                  </Col>
                </Row>

                {/* Botões de Ação */}
                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/tags")}
                    type="button"
                  >
                    <ArrowBack className="me-2" /> Voltar
                  </Button>
                  <Button variant="success" type="submit" disabled={isLoading}>
                    <SaveRounded className="me-2" /> Salvar
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
